<template>
  <section>
    <div class="row">
      <div class="w-100">
        <div v-if="!isReadOnly" class="row">
          <p class="text-danger mb-0">** Notes: Changes you made here will be applied when you save.</p>
        </div>
        <div v-if="!isReadOnly" class="row">
          <span class="mb-1">
            ** To add a new link, enter in the Link's title and url, then press Add. You can create a hierarchical structure using the >> characters
            in your title.
          </span>
          <div class="input-group">
            <input
              v-model="newLink.name"
              type="text"
              class="form-control"
              placeholder="new link name"
              aria-label="new link name"
              :readonly="isReadOnly"
              @keyup.enter.prevent="addLink"
            />
            <input
              v-model="newLink.url"
              type="text"
              class="form-control"
              placeholder="new link url"
              aria-label="new link url"
              :readonly="isReadOnly"
              @keyup.enter.prevent="addLink"
            />
            <button type="button" class="btn btn-primary" tabindex="-1" :disabled="isReadOnly" @click="addLink">Add</button>
          </div>
        </div>
        <div v-if="newLinkMessage" class="row">
          <div class="col text-danger">{{ newLinkMessage }}</div>
        </div>
      </div>
    </div>
    <div v-if="linkGroups.length">
      <div v-for="(group, groupIdx) in linkGroups" :key="groupIdx" class="row">
        <div :id="`panel-${groupIdx}`" class="w-100">
          <div
            v-for="(link, linkIdx) in group.links"
            :key="linkIdx"
            class="row border m-0 mt-2 mb-2"
            :class="{ 'custom-changed-link': isChanged(groupIdx, linkIdx), 'custom-deleted-link': isDeleted(link) }"
          >
            <div class="col">
              <div class="row custom-collapse-header" @click="toggleRelatedLink(groupIdx, linkIdx)">
                <div class="col">
                  <i class="fa" :class="isRelatedExpanded(groupIdx, linkIdx) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                  <span class="ms-2" :class="{ 'custom-deleted-title': isDeleted(link) }">{{
                    documentTitle(groupIdx, linkIdx, link) || link.url
                  }}</span>
                  <span v-if="link.isNew" class="ms-2 badge bg-success">New</span>
                  <span v-if="isDeleted(link)" class="ms-2 badge bg-danger">Removed on save</span>
                </div>
                <!-- removing a row doesn't need the row open, so the action sits in the header -->
                <div v-if="!isReadOnly" class="col-auto custom-row-actions" @click.stop>
                  <button
                    v-if="link.isNew"
                    type="button"
                    class="btn btn-link btn-sm text-danger"
                    title="Remove link"
                    aria-label="Remove link"
                    @click="removeAddedLink(groupIdx, linkIdx)"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                  <!-- a stored link is only marked here; it leaves the payload when the dialog is saved -->
                  <button
                    v-else-if="!isDeleted(link)"
                    type="button"
                    class="btn btn-link btn-sm text-danger"
                    title="Remove link"
                    aria-label="Remove link"
                    @click="removeStoredLink(groupIdx, linkIdx)"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                  <button
                    v-else
                    type="button"
                    class="btn btn-link btn-sm text-secondary"
                    title="Undo remove"
                    aria-label="Undo remove"
                    @click="restoreStoredLink(link)"
                  >
                    <i class="fas fa-undo"></i>
                  </button>
                </div>
              </div>
              <div v-show="isRelatedExpanded(groupIdx, linkIdx)">
                <div class="row mt-2">
                  <div class="col">
                    <div class="input-group">
                      <span class="input-group-text">Document Title</span>
                      <input
                        :value="documentTitle(groupIdx, linkIdx, link)"
                        type="text"
                        class="form-control"
                        :readonly="isReadOnly || isDeleted(link)"
                        @input="documentTitleChanged(groupIdx, linkIdx, $event.target.value)"
                      />
                    </div>
                  </div>
                </div>
                <div class="row mt-2 mb-2">
                  <div class="col">
                    <div class="input-group">
                      <span class="input-group-text">Url</span>
                      <!-- a link the admin just typed in is still editable; a stored one is not -->
                      <input
                        :value="link.url"
                        type="text"
                        class="form-control"
                        :readonly="isReadOnly || !link.isNew || isDeleted(link)"
                        @input="newLinkUrlChanged(link, $event.target.value)"
                      />
                      <a
                        v-if="link.url"
                        class="btn btn-outline-secondary"
                        :href="link.url"
                        title="open link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i class="fa fa-external-link-alt"></i>
                      </a>
                      <button
                        v-if="!isReadOnly && !link.isNew"
                        type="button"
                        class="btn btn-outline-danger btn-sm"
                        :disabled="isReadOnly || isDeleted(link) || isSearching(groupIdx, linkIdx)"
                        @click="brokenLinkClicked(link, groupIdx, linkIdx)"
                      >
                        Broken Link
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="searchFor(groupIdx, linkIdx)">
                  <span v-if="searchFor(groupIdx, linkIdx).loading">
                    <i class="fas fa-circle-notch fa-spin"></i> Searching for alternative link(s)...
                  </span>
                  <span v-else-if="searchFor(groupIdx, linkIdx).error" class="suggested-status suggested-error">
                    <i class="fas fa-exclamation-triangle"></i> We were unable to search for replacement links right now. Please try again later.
                  </span>
                  <div v-else-if="searchFor(groupIdx, linkIdx).links.length > 0">
                    <h6 class="mt-1">
                      Replacement Links <span>(<span class="text-danger">* Select the link that best matches the link title above.</span>)</span>
                    </h6>
                    <div
                      v-for="(suggestion, sIdx) in searchFor(groupIdx, linkIdx).links"
                      :key="sIdx"
                      class="row"
                      :class="{ 'custom-selected-link': isSelected(groupIdx, linkIdx, sIdx) }"
                    >
                      <div class="col">
                        <div class="row custom-collapse-header" @click="toggleLink(groupIdx, linkIdx, sIdx)">
                          <div class="col">
                            <i class="fa" :class="isExpanded(groupIdx, linkIdx, sIdx) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                            <span class="ms-2">{{ suggestion.name }}</span>
                            <span class="ms-2" style="color: green">{{ suggestion.relevance || 0 }} % match</span>
                          </div>
                          <div class="col-auto custom-replace-check" @click.stop>
                            <div class="form-check">
                              <input
                                :id="`replace-${groupIdx}-${linkIdx}-${sIdx}`"
                                :checked="isSelected(groupIdx, linkIdx, sIdx)"
                                type="checkbox"
                                class="form-check-input"
                                :disabled="isReadOnly"
                                @change="toggleSelected(groupIdx, linkIdx, sIdx)"
                              />
                              <label :for="`replace-${groupIdx}-${linkIdx}-${sIdx}`" class="form-check-label"></label>
                            </div>
                          </div>
                        </div>
                        <div v-show="isExpanded(groupIdx, linkIdx, sIdx)">
                          <div class="row m-2">
                            <div class="col">
                              <div class="input-group">
                                <span class="input-group-text">API SELF HREF:</span>
                                <input :value="suggestion.url" type="text" class="form-control" readonly />
                                <a v-if="suggestion.url" class="btn btn-outline-secondary" :href="suggestion.url" title="open link" target="_blank">
                                  <i class="fa fa-external-link-alt"></i>
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="row mt-3">
      <div class="col text-center">No related links found.</div>
    </div>
  </section>
</template>
<script>
import { mapActions } from 'pinia'
import { adasmatrixStore } from '@/stores/adasmatrix'
import { buildAlternateLinks, buildManualLink, findTargetGroupIndex, parseLink } from '@/plugins/broken-link-helper'

export default {
  name: 'RepairLinkRelatedLinks',
  props: {
    groups: {
      type: Array,
      default: () => []
    },
    // ALLDATA can return a long tail of weak matches -- only the best few are worth offering
    numbOfSearchResults: {
      type: Number,
      default: 5
    },
    model: {
      type: Object,
      default: () => ({})
    },
    // the view decides, so the tab can't fall out of step with it
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  emits: ['change'],
  data() {
    return {
      // every map below is keyed per related link (`${groupIdx}-${linkIdx}`) so that
      // editing one row never touches another row -- or the reported link on the other tab
      brokenLinks: {},
      expandedRelatedLinks: {},
      expandedLinks: {},
      selectedLinks: {},
      documentTitles: {},
      // links the admin has typed in on this tab -- they are merged into `linkGroups`
      // below and only reach ALLDATA's stored payload once the dialog is saved
      addedLinks: [],
      // stored links the admin has marked for removal, by their ALLDATA self href.
      // They stay on screen (struck through, undoable) until the dialog is saved.
      deletedLinks: [],
      newLink: {
        name: '',
        url: ''
      },
      newLinkMessage: ''
    }
  },
  computed: {
    isReadOnly() {
      return this.readOnly
    },
    /** The stored groups, with anything the admin has added on this tab merged in. */
    linkGroups() {
      if (!this.addedLinks.length) {
        return this.groups
      }
      let groups = [...this.groups]
      if (!groups.length) {
        groups.push({
          group: (this.model && this.model.categoryName) || '',
          links: []
        })
      }
      let target = this.targetGroupIndex(groups)
      groups[target] = {
        ...groups[target],
        links: [...(groups[target].links || []), ...this.addedLinks]
      }
      return groups
    }
  },
  watch: {
    groups() {
      this.resetChanges()
    }
  },
  methods: {
    ...mapActions(adasmatrixStore, ['getLinksByTypeAndSearchTerm']),
    linkKey(groupIdx, linkIdx) {
      return `${groupIdx}-${linkIdx}`
    },
    resetChanges() {
      this.brokenLinks = {}
      this.expandedRelatedLinks = {}
      this.expandedLinks = {}
      this.selectedLinks = {}
      this.documentTitles = {}
      this.addedLinks = []
      this.deletedLinks = []
      this.newLink = { name: '', url: '' }
      this.newLinkMessage = ''
      this.emitChanges()
    },
    /**
     * The group a manually added link belongs in: the one the report was filed against.
     * Shares findTargetGroupIndex with the save, so the group the admin sees a link
     * previewed in is the group it is written to.
     */
    targetGroupIndex(groups) {
      let idx = findTargetGroupIndex(
        groups.map((group) => ({
          groupCategory: {
            id: (group.links || []).length ? group.links[0].category : null,
            name: group.group || ''
          }
        })),
        {
          groupCategoryId: this.model ? this.model.categoryId : null,
          groupCategoryName: (this.model && this.model.categoryName) || ''
        },
        null
      )
      return idx === -1 ? 0 : idx
    },
    hasUrl(url) {
      return this.linkGroups.some((group) => (group.links || []).some((link) => link.url === url))
    },
    addLink() {
      if (this.isReadOnly) {
        return
      }
      let name = String(this.newLink.name || '').trim()
      let url = String(this.newLink.url || '').trim()
      this.newLinkMessage = ''
      if (!name || !url) {
        this.newLinkMessage = 'Enter both a title and a url for the new link.'
        return
      }
      if (this.hasUrl(url)) {
        this.newLinkMessage = 'That url is already in this list.'
        return
      }
      this.addedLinks.push(buildManualLink(name, url, this.model ? this.model.categoryId : null))
      this.newLink = { name: '', url: '' }
      this.emitChanges()
      this.$nextTick(() => this.refreshPanelHeight(this.targetGroupIndex(this.linkGroups)))
    },
    newLinkUrlChanged(link, value) {
      if (!link.isNew) {
        return
      }
      link.url = value
      this.emitChanges()
    },
    removeAddedLink(groupIdx, linkIdx) {
      let link = this.linkAt(groupIdx, linkIdx)
      let position = link ? this.addedLinks.indexOf(link) : -1
      if (position === -1) {
        return
      }
      this.addedLinks.splice(position, 1)
      this.forgetLinkAt(groupIdx, linkIdx)
      this.newLinkMessage = ''
      this.emitChanges()
      this.$nextTick(() => this.refreshPanelHeight(groupIdx))
    },
    /** What a save matches a stored row on: its ALLDATA self href. */
    linkRef(link) {
      return link ? String(link.href || link.url || '') : ''
    },
    isDeleted(link) {
      let ref = this.linkRef(link)
      return ref !== '' && this.deletedLinks.indexOf(ref) !== -1
    },
    removeStoredLink(groupIdx, linkIdx) {
      if (this.isReadOnly) {
        return
      }
      let link = this.linkAt(groupIdx, linkIdx)
      let ref = this.linkRef(link)
      if (!link || link.isNew || ref === '' || this.isDeleted(link)) {
        return
      }
      this.deletedLinks.push(ref)
      // a row on its way out has nothing to replace
      this.selectedLinks[this.linkKey(groupIdx, linkIdx)] = null
      this.emitChanges()
      this.$nextTick(() => this.refreshPanelHeight(groupIdx))
    },
    restoreStoredLink(link) {
      let position = this.deletedLinks.indexOf(this.linkRef(link))
      if (position === -1) {
        return
      }
      this.deletedLinks.splice(position, 1)
      this.emitChanges()
    },
    /** Drop the per-row state for a removed row, so the rows below it keep their own. */
    forgetLinkAt(groupIdx, linkIdx) {
      this.brokenLinks = this.shiftKeys(this.brokenLinks, groupIdx, linkIdx)
      this.expandedRelatedLinks = this.shiftKeys(this.expandedRelatedLinks, groupIdx, linkIdx)
      this.expandedLinks = this.shiftKeys(this.expandedLinks, groupIdx, linkIdx)
      this.selectedLinks = this.shiftKeys(this.selectedLinks, groupIdx, linkIdx)
      this.documentTitles = this.shiftKeys(this.documentTitles, groupIdx, linkIdx)
    },
    shiftKeys(map, groupIdx, linkIdx) {
      let shifted = {}
      Object.keys(map).forEach((key) => {
        // keys are `${groupIdx}-${linkIdx}`, with a trailing suggestion index on some
        let parts = key.split('-')
        let group = Number(parts[0])
        let link = Number(parts[1])
        if (group !== groupIdx || link < linkIdx) {
          shifted[key] = map[key]
          return
        }
        if (link === linkIdx) {
          return
        }
        parts[1] = String(link - 1)
        shifted[parts.join('-')] = map[key]
      })
      return shifted
    },
    /** The in-flight / completed ALLDATA search for one related link, if it has been asked for. */
    searchFor(groupIdx, linkIdx) {
      return this.brokenLinks[this.linkKey(groupIdx, linkIdx)] || null
    },
    isSearching(groupIdx, linkIdx) {
      let found = this.searchFor(groupIdx, linkIdx)
      return !!(found && found.loading)
    },
    isRelatedExpanded(groupIdx, linkIdx) {
      return this.expandedRelatedLinks[this.linkKey(groupIdx, linkIdx)] === true
    },
    toggleRelatedLink(groupIdx, linkIdx) {
      let key = this.linkKey(groupIdx, linkIdx)
      this.expandedRelatedLinks[key] = !this.expandedRelatedLinks[key]
      this.$nextTick(() => this.refreshPanelHeight(groupIdx))
    },
    isExpanded(groupIdx, linkIdx, sIdx) {
      return this.expandedLinks[`${this.linkKey(groupIdx, linkIdx)}-${sIdx}`] === true
    },
    toggleLink(groupIdx, linkIdx, sIdx) {
      let key = `${this.linkKey(groupIdx, linkIdx)}-${sIdx}`
      this.expandedLinks[key] = !this.expandedLinks[key]
    },
    isSelected(groupIdx, linkIdx, sIdx) {
      return this.selectedLinks[this.linkKey(groupIdx, linkIdx)] === sIdx
    },
    toggleSelected(groupIdx, linkIdx, sIdx) {
      // only one replacement link can be selected per related link
      let key = this.linkKey(groupIdx, linkIdx)
      this.selectedLinks[key] = this.selectedLinks[key] === sIdx ? null : sIdx
      this.emitChanges()
    },
    /** The edited title if the admin has typed one, otherwise what ALLDATA reported. */
    documentTitle(groupIdx, linkIdx, link) {
      let key = this.linkKey(groupIdx, linkIdx)
      return this.documentTitles[key] === undefined ? link.name : this.documentTitles[key]
    },
    documentTitleChanged(groupIdx, linkIdx, value) {
      this.documentTitles[this.linkKey(groupIdx, linkIdx)] = value
      this.emitChanges()
    },
    /**
     * A row counts as changed once its title differs or a replacement is picked --
     * and a row the admin added, or marked for removal, is a change in its own right.
     */
    isChanged(groupIdx, linkIdx) {
      let link = this.linkAt(groupIdx, linkIdx)
      if (!link) {
        return false
      }
      if (link.isNew || this.isDeleted(link)) {
        return true
      }
      let key = this.linkKey(groupIdx, linkIdx)
      let title = this.documentTitles[key]
      // compare against what documentTitle() falls back to, or an untouched row reads as edited
      let titleChanged = title !== undefined && title.trim() !== String(link.name || '').trim()
      return titleChanged || this.selectedUrl(groupIdx, linkIdx) !== ''
    },
    linkAt(groupIdx, linkIdx) {
      let group = this.linkGroups[groupIdx]
      if (!group || !group.links) {
        return null
      }
      return group.links[linkIdx] || null
    },
    selectedUrl(groupIdx, linkIdx) {
      let key = this.linkKey(groupIdx, linkIdx)
      let sIdx = this.selectedLinks[key]
      if (sIdx === null || sIdx === undefined) {
        return ''
      }
      let found = this.brokenLinks[key]
      let suggestion = found && found.links ? found.links[sIdx] : null
      return suggestion ? suggestion.url : ''
    },
    /**
     * The rows the admin has actually touched, in the shape the fix endpoint takes:
     * { originalUrl, documentTitle, newUrl, isNew, isDeleted }. A row with `isNew` has
     * no original to repair -- the endpoint appends it to the group instead -- and a row
     * with `isDeleted` is taken out of the group rather than edited.
     *
     * `originalUrl` is the stored self href, not the displayed url, so the endpoint can
     * match the row in the payload it saved.
     */
    buildChanges() {
      let changes = []
      this.linkGroups.forEach((group, groupIdx) => {
        ;(group.links || []).forEach((link, linkIdx) => {
          if (!this.isChanged(groupIdx, linkIdx)) {
            return
          }
          let documentTitle = String(this.documentTitle(groupIdx, linkIdx, link) || '').trim()
          if (link.isNew) {
            changes.push({
              originalUrl: '',
              documentTitle: documentTitle,
              newUrl: String(link.url || '').trim(),
              isNew: true,
              isDeleted: false
            })
            return
          }
          if (this.isDeleted(link)) {
            changes.push({
              originalUrl: this.linkRef(link),
              documentTitle: documentTitle,
              newUrl: '',
              isNew: false,
              isDeleted: true
            })
            return
          }
          changes.push({
            originalUrl: this.linkRef(link),
            documentTitle: documentTitle,
            newUrl: this.selectedUrl(groupIdx, linkIdx),
            isNew: false,
            isDeleted: false
          })
        })
      })
      return changes
    },
    emitChanges() {
      this.$emit('change', this.buildChanges())
    },
    refreshPanelHeight(idx) {
      let accPanel = document.getElementById(`panel-${idx}`)
      if (accPanel && accPanel.style.maxHeight) {
        accPanel.style.maxHeight = accPanel.scrollHeight + 'px'
      }
    },
    async brokenLinkClicked(link, groupIdx, linkIdx) {
      let key = this.linkKey(groupIdx, linkIdx)
      this.brokenLinks[key] = { loading: true, error: false, links: [] }
      this.$nextTick(() => this.refreshPanelHeight(groupIdx))
      // the suggestion indexes are about to change, so any pick against the old list is stale
      this.selectedLinks[key] = null
      this.emitChanges()
      let search = parseLink(link)
      try {
        let results = await this.getLinksByTypeAndSearchTerm({
          source: this.model ? this.model.source : null,
          carid: search.carid,
          searchTerm: search.searchTerm,
          itype: search.itype
        })
        let suggestions = buildAlternateLinks(results)
          .filter((suggestion) => suggestion.url !== link.url)
          .slice(0, this.numbOfSearchResults)
        this.brokenLinks[key] = {
          loading: false,
          error: false,
          links: suggestions
        }
      } catch {
        this.brokenLinks[key] = { loading: false, error: true, links: [] }
      }
      this.$nextTick(() => this.refreshPanelHeight(groupIdx))
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-collapse-header {
  cursor: pointer;
  padding: 0.5rem 0;
  user-select: none;
}
.custom-replace-check {
  cursor: default;
  white-space: nowrap;
}
.custom-replace-check .form-check-label {
  cursor: pointer;
}
.custom-row-actions {
  cursor: default;
  white-space: nowrap;
}
.custom-row-actions .btn-link {
  padding: 0 0.25rem;
  text-decoration: none;
}
.custom-selected-link {
  background-color: #e4f5df;
}
.custom-changed-link {
  border-color: #7cb342 !important;
}
.custom-deleted-link {
  background-color: #fdeaea;
  border-color: #d9534f !important;
}
.custom-deleted-title {
  text-decoration: line-through;
  opacity: 0.7;
}
</style>
